import { APP_INITIALIZER, LOCALE_ID, NgModule, DEFAULT_CURRENCY_CODE } from "@angular/core";
import { HttpClientModule } from "@angular/common/http";
import { RouterModule, Routes } from "@angular/router";
import { SharedModule } from "@shared/shared.module";
import { BrowserModule } from "@angular/platform-browser";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { FuseProgressBarModule, FuseSidebarModule } from "@fuse/components";
import { LayoutModule } from "./layout/layout.module";
import { FuseSharedModule } from "@fuse/shared.module";
import { SESSION_COOKIE_NAME } from "@api";
import { TokenAuthServiceProxy, UserServiceProxy } from "@api/core";
import { LEGACY_API_BASE_URL } from "@api/runtime";
import { FuseModule } from "@fuse/fuse.module";
import { fuseConfig } from "./fuse-config";
import { ServiceProxyModule } from "@shared/api/service-proxy.module";
import { TranslateModule } from "@ngx-translate/core";
import { AppConsts } from "@shared/AppConsts";
import { AppComponent } from "app/app.component";
import localePt from "@angular/common/locales/pt";
import { registerLocaleData } from "@angular/common";
import { FacebookPixelService } from "@app/FacebookPixelService";
import { AbpModule } from "abp-ng2-module";
import { MAT_DATE_LOCALE, MatNativeDateModule } from "@angular/material/core";
import { MAT_DIALOG_DEFAULT_OPTIONS } from "@angular/material/dialog";
import { AppInitializer } from "../app-initializer";
import { SncrBrokerCallbackService } from "./wapps/system/integration/sncr/sncr-broker-callback.service";
import { DIGITAL_CERTIFICATE_REGISTRATION } from "@vhealth/professional/digital-certificate";
import { DigitalCertificateRegistrationService } from "./wapps/signature/digital-certificate-registration.service";
import { CommonModule, CurrencyPipe } from "@angular/common";
import { IMaskModule } from "angular-imask";
import { UsersService } from "./wapps/system/users/users.service";
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from "@angular/material/form-field";
import { ReactiveFormsModule } from "@angular/forms";
import { FormlyModule } from "@ngx-formly/core";
import { PendingTermsOfServiceSignatureModule } from "@app/wapps/legal/pending-terms-of-service/pending-terms-of-service-signature.module";
import { InsightsModule } from "@app/monitoring.module";
import { AudioService } from "@app/audio.service";
import { environment } from "../environments/environment";
import { GoogleTagManagerModule } from "angular-google-tag-manager";
import { HtmlInjectorService } from "@app/HtmlInjectorService";
import { TranslocoCoreModule } from "@i18n";
import { StoreModule } from "@ngrx/store";
import { EffectsModule } from "@ngrx/effects";
import { storeDevtoolsImports } from "./store-devtools";
import { StoreRouterConnectingModule } from "@ngrx/router-store";
import { FUSE_SCREENS, FuseConfigModule, FuseScrollbarModule, NAVBAR_LOADING } from "@nx-workspace/fuse";
import { FuseModule as FuseV2Module } from "@nx-workspace/fuse";
import { FuseProgressBarService } from "@fuse/components/progress-bar/progress-bar.service";
import { UiNotifyModule } from "@ui/notify";
import { STATISTICS_API_BASE_URL } from "@dashboard-statistics/api/base-url";
import { FINANCIAL_API_BASE_URL } from "@financial/api/base-url";
import { WEBHOOKS_API_BASE_URL } from "@webhooks/api/base-url";
import { AuthService } from "@vadmin/account/auth";
import { MatButtonModule } from "@angular/material/button";
import { MatCardModule } from "@angular/material/card";
import { MatIconModule } from "@angular/material/icon";
import { SMART_FORMS_API_BASE_URL } from "@vhealth/engage/api/base-url";
import { IconsModule } from "@vui/icons";
import { NotificationService } from "./wapps/notification/notification.service";
import { GeocodeService } from "./wapps/core/address/geocode.service";
import { GoogleMapsLoaderService } from "./wapps/core/maps-api-loader.service";
import { SUBSCRIPTION_API_BASE_URL } from "@subscription/proxies";
import {
    dateNotInFutureValidationMessage,
    dateNotInFutureValidator,
    dumAndDppOrderValidator,
    EmailValidator,
    minItemsValidationMessage,
    minItemsValidator,
} from "@vhealth/vforms/validators";
import { PJBANK_API_BASE_URL, PJBANK_ID, VINDI_API_BASE_URL, VINDI_TOKEN } from "@subscription/card";
import { NavigationBuilderService } from "./navigation/navigation-builder.service";
import { MEDICATION_CATALOG_API_BASE_URL, MedicationCatalogApiModule } from "@medication-catalog/api";

const appRoutes: Routes = [
    {
        path: "",
        loadChildren: () => import("./wapps/wapps.module").then((m) => m.WappsModule),
        data: { preload: true },
    },
];

export function getRemoteServiceBaseUrl(): string {
    AppConsts.appBaseUrl = location.origin;
    let apiUrl = environment.api_url;

    if (apiUrl?.length === 0) {
        if (!document.location.origin) {
            const port = document.location.port ? ":" + document.location.port : "";
            apiUrl = document.location.protocol + "//" + document.location.hostname + port;
        } else {
            apiUrl = document.location.origin;
        }
    }

    AppConsts.remoteServiceBaseUrl = apiUrl;

    return AppConsts.remoteServiceBaseUrl;
}

export function getCurrentLanguage(): string {
    return abp.localization.currentLanguage.name;
}

export function getRemoteStatisticsApiBaseUrl(): string {
    let baseUrl = "";
    if (environment.statistics_api_url !== undefined) {
        baseUrl = environment.statistics_api_url;
    } else {
        if (!document.location.origin) {
            const port = document.location.port ? ":" + document.location.port : "";
            baseUrl = document.location.protocol + "//" + document.location.hostname + port;
        } else {
            baseUrl = document.location.origin;
        }
    }
    return baseUrl;
}

export function getRemoteSubscriptionApiBaseUrl(): string {
    let baseUrl = "";
    if (environment.subscription_api_url !== undefined) {
        baseUrl = environment.subscription_api_url;
    } else {
        if (!document.location.origin) {
            const port = document.location.port ? ":" + document.location.port : "";
            baseUrl = document.location.protocol + "//" + document.location.hostname + port;
        } else {
            baseUrl = document.location.origin;
        }
    }
    return baseUrl;
}

export function getRemoteMedicationCatalogApiBaseUrl(): string {
    if (environment.medication_catalog_api_url !== undefined) {
        return environment.medication_catalog_api_url;
    }

    return getRemoteServiceBaseUrl();
}

export function getRemoteFinancialApiBaseUrl(): string {
    let baseUrl = "";
    if (environment.financial_api_url !== undefined) {
        baseUrl = environment.financial_api_url;
    } else {
        if (!document.location.origin) {
            const port = document.location.port ? ":" + document.location.port : "";
            baseUrl = document.location.protocol + "//" + document.location.hostname + port;
        } else {
            baseUrl = document.location.origin;
        }
    }
    return baseUrl;
}

export function getRemoteWebhooksApiBaseUrl(): string {
    let baseUrl = "";
    if (environment.webhooks_api_url !== undefined) {
        baseUrl = environment.webhooks_api_url;
    } else {
        if (!document.location.origin) {
            const port = document.location.port ? ":" + document.location.port : "";
            baseUrl = document.location.protocol + "//" + document.location.hostname + port;
        } else {
            baseUrl = document.location.origin;
        }
    }
    return baseUrl;
}

registerLocaleData(localePt);

@NgModule({
    declarations: [AppComponent],
    imports: [
        CommonModule,
        BrowserModule,
        BrowserAnimationsModule,
        HttpClientModule,
        RouterModule.forRoot(appRoutes, { enableTracing: false }),
        TranslateModule.forRoot(),
        // Fuse modules
        FuseModule.forRoot(fuseConfig),
        StoreModule.forRoot({}),
        EffectsModule.forRoot([]),
        FuseProgressBarModule,
        FuseSharedModule,
        FuseSidebarModule,
        LayoutModule,
        //abp
        SharedModule.forRoot(),
        AbpModule,
        ServiceProxyModule,

        IMaskModule,
        ReactiveFormsModule,
        FormlyModule.forRoot({
            validators: [
                { name: "email", validation: EmailValidator },
                { name: "minItems", validation: minItemsValidator },
                { name: "dateOrder", validation: dumAndDppOrderValidator },
                { name: "dateNotInFuture", validation: dateNotInFutureValidator },
            ],
            extras: {
                resetFieldOnHide: true,
            },
            validationMessages: [
                { name: "required", message: "Campo obrigatório" },
                { name: "email", message: "E-mail inválido" },
                { name: "minItems", message: minItemsValidationMessage },
                { name: "dateOrder", message: "DUM deve ser anterior à DPP." },
                { name: "dateNotInFuture", message: dateNotInFutureValidationMessage },
                { name: "invalidPhone", message: "Telefone inválido" },
            ],
        }),
        PendingTermsOfServiceSignatureModule,
        InsightsModule,

        GoogleTagManagerModule.forRoot({
            id: environment.gtmId,
        }),
        FuseV2Module,
        FuseConfigModule.forRoot({
            layout: "dense",
            scheme: "light",
            theme: "default",
            screens: FUSE_SCREENS,
        }),
        FuseScrollbarModule,
        TranslocoCoreModule,
        UiNotifyModule,
        // Registers the icon sets and the namespace-less unicons resolver; the `@vui` barrel would pull
        // every vui and Material component into the initial bundle. (VS-1961)
        IconsModule,
        MatNativeDateModule,

        // 3rd party modules that require global configuration via forRoot
        ...storeDevtoolsImports,
        StoreRouterConnectingModule.forRoot(),
        MatButtonModule,
        MatCardModule,
        MatIconModule,
        MedicationCatalogApiModule,
    ],
    exports: [RouterModule],
    providers: [
        { provide: DIGITAL_CERTIFICATE_REGISTRATION, useExisting: DigitalCertificateRegistrationService },
        UsersService,
        NavigationBuilderService,
        { provide: NAVBAR_LOADING, useExisting: FuseProgressBarService },
        {
            provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
            useValue: {
                appearance: "outline",
                floatLabel: "auto",
                subscriptSizing: "dynamic",
                hideRequiredMarker: false,
            },
        },
        { provide: DEFAULT_CURRENCY_CODE, useValue: "R$" },
        { provide: MAT_DATE_LOCALE, useValue: "pt-BR" },
        // Dialogs opened from root-scoped code (AppComponent, NotificationService) resolve their defaults here. (VS-1961)
        { provide: MAT_DIALOG_DEFAULT_OPTIONS, useValue: { panelClass: "mat-dialog-override", hasBackdrop: true } },
        // Every lazy module used to get CurrencyPipe from the deleted CustomMaterialModule. (VS-1961)
        CurrencyPipe,
        {
            provide: APP_INITIALIZER,
            useFactory: (appInitializer: AppInitializer) => appInitializer.init(),
            deps: [AppInitializer],
            multi: true,
        },
        {
            provide: APP_INITIALIZER,
            useFactory: (brokerCallback: SncrBrokerCallbackService) => () => brokerCallback.captureFromLandingUrl(),
            deps: [SncrBrokerCallbackService],
            multi: true,
        },
        {
            provide: LOCALE_ID,
            useFactory: getCurrentLanguage,
        },
        FacebookPixelService,
        AudioService,
        HtmlInjectorService,
        { provide: STATISTICS_API_BASE_URL, useFactory: getRemoteStatisticsApiBaseUrl },
        { provide: MEDICATION_CATALOG_API_BASE_URL, useFactory: getRemoteMedicationCatalogApiBaseUrl },
        { provide: SESSION_COOKIE_NAME, useValue: "Abp.AuthToken" },
        { provide: LEGACY_API_BASE_URL, useFactory: getRemoteServiceBaseUrl },
        { provide: SMART_FORMS_API_BASE_URL, useFactory: getRemoteServiceBaseUrl },
        { provide: SUBSCRIPTION_API_BASE_URL, useFactory: getRemoteSubscriptionApiBaseUrl },
        { provide: FINANCIAL_API_BASE_URL, useFactory: getRemoteFinancialApiBaseUrl },
        { provide: WEBHOOKS_API_BASE_URL, useFactory: getRemoteWebhooksApiBaseUrl },
        TokenAuthServiceProxy,
        UserServiceProxy,
        AuthService,
        NotificationService,
        GeocodeService,
        GoogleMapsLoaderService,
        { provide: PJBANK_API_BASE_URL, useValue: environment.pjbank_api_url },
        { provide: PJBANK_ID, useValue: environment.pjbank_id },
        { provide: VINDI_API_BASE_URL, useValue: environment.vindi_api_url },
        { provide: VINDI_TOKEN, useValue: environment.vindi_public_token },
    ],
    bootstrap: [AppComponent],
})
export class AppModule {
    constructor() {}
}
